import axios from "axios";
import client from "./client";

export interface UserInMap {
  userId: string;
  name: string | null;
  firstName: string | null;
  lastName: string | null;
  avatar: string | null;
  lat: number;
  lng: number;
  online: boolean;
  talkAbout: string | null;
  bio?: string | null;
  ringColor: string | null;
  streetChatId: number | null;
  convId: number | null;
  status?: number;
  /** Present only when the map request carried a search term. */
  matched?: boolean;
  /** Field names that matched: firstName | lastName | talkAbout | bio. */
  matchedFields?: string[];
}

export const STATUS_ME = 0;
export const STATUS_RELATED_TO_ME = 1;
export const STATUS_OTHER = 2;

export interface GpsBounds {
  lat1: number; // SW latitude
  lng1: number; // SW longitude
  lat2: number; // NE latitude
  lng2: number; // NE longitude
}

export async function getUsersByGps(bounds: GpsBounds): Promise<UserInMap[]> {
  const res = await client.get<{ result: UserInMap[] }>("/api/personal/gps", {
    params: bounds,
  });
  return res.data.result;
}

export interface HeatmapCell {
  lat: number;
  lng: number;
  weight: number;
}

export interface MapViewResult {
  mode: "markers" | "heatmap";
  users: UserInMap[] | null;
  cells: HeatmapCell[] | null;
  maxWeight: number | null;
}

/**
 * Unified viewport query: the server decides whether this viewport gets
 * per-user markers or an aggregated density heatmap (privacy threshold).
 */
export async function getMapView(
  bounds: GpsBounds,
  zoom: number,
  q?: string,
): Promise<MapViewResult> {
  // A blank term must send no `q` at all, not `q=` — the client owns this
  // rather than leaning on the server's blank handling.
  const trimmed = q?.trim();
  const params = trimmed
    ? { ...bounds, zoom, q: trimmed }
    : { ...bounds, zoom };
  const res = await client.get<{ result: MapViewResult }>("/api/personal/map", {
    params,
  });
  return res.data.result;
}

export interface NearestMarker {
  lat: number;
  lng: number;
}

/**
 * Nearest user marker to a clicked point, resolved server-side with no distance
 * bound. Resolves null only when no located user exists at all (HTTP 204).
 */
export async function getNearestMarker(
  lat: number,
  lng: number,
): Promise<NearestMarker | null> {
  const res = await client.get<{ result: NearestMarker } | "">(
    "/api/personal/map/nearest",
    { params: { lat, lng } },
  );
  if (res.status === 204 || !res.data) return null;
  return (res.data as { result: NearestMarker }).result;
}

export interface MyPersonal {
  userId: string;
  name: string | null;
  firstName: string | null;
  lastName: string | null;
  avatar: string | null;
  lat: number | null;
  lng: number | null;
  talkAbout: string | null;
  bio: string | null;
  ringColor: string | null;
  streetChatId: number | null;
  convId: number | null;
  hasInformation?: boolean;
  // main-api's shared PersonalResponse carries the card counts on GET /api/personal too.
  followerCount?: number;
  followingCount?: number;
  joinMeCount?: number;
  postCount?: number;
}

export async function getMyPersonal(): Promise<MyPersonal> {
  const res = await client.get<{ result: MyPersonal }>("/api/personal");
  return res.data.result;
}

// Response matches Android's BigPersonal; only the join-flow fields are typed.
export interface PersonalById {
  streetChatId?: number | null;
  convId?: number | null;
  followerCount?: number;
  followingCount?: number;
  joinMeCount?: number;
  postCount?: number;
  [key: string]: unknown;
}

export async function getPersonal(userId: string): Promise<PersonalById> {
  const res = await client.get<{ result: PersonalById }>(
    `/api/personal/${userId}`,
  );
  return res.data.result;
}

export interface UserById {
  userId: string;
  name: string | null;
  firstName: string | null;
  lastName: string | null;
  avatar: string | null;
}

/** Batch user lookup shared with Android (GET /api/personal/by-ids?ids=a,b,c). */
export async function getUsersByIds(ids: string[]): Promise<UserById[]> {
  if (ids.length === 0) return [];
  const res = await client.get<{ result: UserById[] }>("/api/personal/by-ids", {
    params: { ids: ids.join(",") },
  });
  return res.data.result ?? [];
}

export interface ProfileCardPatch {
  firstName?: string;
  lastName?: string;
  /** Blank clears the avatar. */
  avatarFileId?: string;
  /** 6 hex digits; blank clears. */
  ringColor?: string;
}

/** Card fields (name, avatar, ring colour) live in iam-stc; `name` is server-computed. */
export async function updateProfileCard(
  patch: ProfileCardPatch,
): Promise<void> {
  await client.patch("/iam/api/v1/profile", patch);
}

/** About fields only (talkAbout, bio); card fields go through updateProfileCard. */
export async function updatePersonal(
  data: Record<string, unknown>,
): Promise<void> {
  await client.patch("/api/personal", data);
}

/** True when PATCH /api/personal refused a bio over 150 code points (400 {"code":"bio_too_long"}). */
export function isBioTooLong(e: unknown): boolean {
  return (
    axios.isAxiosError(e) &&
    e.response?.status === 400 &&
    (e.response.data as { code?: unknown } | undefined)?.code === "bio_too_long"
  );
}

export type SuggestedMeta = "FOLLOWS_YOU" | "FOLLOWED_BY" | "ON_MAP";
export interface SuggestedUser {
  user: {
    id: string; firstName?: string | null; lastName?: string | null; name?: string | null;
    avatar?: string | null; ringColor?: string | null;
  };
  meta: SuggestedMeta;
  followedByName?: string | null;
}

/** People you may know; the server wraps the rows as a ListResponse (`result`). */
export async function getSuggested(limit = 20): Promise<SuggestedUser[]> {
  const res = await client.get<{ result: SuggestedUser[] }>("/api/personal/suggested", { params: { limit } });
  return res.data.result ?? [];
}

export async function createLocation(lat: number, lng: number): Promise<void> {
  await client.put("/api/personal/create", { lat, lng });
}

export async function moveLocation(lat: number, lng: number): Promise<void> {
  await client.put("/api/personal/move", { lat, lng });
}

export async function removeLocation(): Promise<void> {
  await client.put("/api/personal/remove");
}
